// Row-selection helpers for MediaTable's "select" mode.
//
// Selection is the set of public_ids that will be handed to the API — not the DOM — so it
// can also cover rows that DataTables has not rendered (other pages). These helpers keep the
// per-row DOM (the data-selected flag and the checkmark button colour) in sync with that set.

export const SELECTED_ROW_BACKGROUND = "rgba(50, 166, 91, 1)";
export const UNSELECTED_ROW_BACKGROUND = "rgba(50, 166, 91, 0.3)";

// Each selectable row is rendered as:
//   <div data-public-id="..." data-selected="false|true"><button .../></div>
export const getSelectableRows = (container) =>
  Array.from(
    container?.querySelectorAll("[data-public-id][data-selected]") ?? []
  );

export const setRowSelected = (row, selected, selectionSet) => {
  if (!row || !row.dataset || !selectionSet) return;

  const publicId = row.dataset.publicId;
  if (!publicId) return;

  row.dataset.selected = selected ? "true" : "false";

  const button = row.querySelector("button");
  if (button) {
    button.style.backgroundColor = selected
      ? SELECTED_ROW_BACKGROUND
      : UNSELECTED_ROW_BACKGROUND;
  }

  if (selected) {
    selectionSet.add(publicId);
  } else {
    selectionSet.delete(publicId);
  }
};

export const toggleRowSelected = (row, selectionSet) => {
  if (!row || !selectionSet) return;
  setRowSelected(row, row.dataset?.selected !== "true", selectionSet);
};

/**
 * Select or deselect every item.
 *
 * @param {object} options
 * @param {Element|null} options.container Table container; rows inside it get their DOM
 *   state updated. Rows for other pages may not exist yet, hence `ids`.
 * @param {Array<string>} options.ids Every selectable public_id in the list.
 * @param {boolean} options.selected Target state.
 * @param {Set<string>} options.selectionSet Set to mutate in place.
 * @returns {number} The resulting selection size.
 */
export const setSelection = ({ container, ids = [], selected = true, selectionSet }) => {
  if (!selectionSet) return 0;

  getSelectableRows(container).forEach((row) =>
    setRowSelected(row, selected, selectionSet)
  );

  ids.forEach((id) => {
    if (!id) return;
    if (selected) {
      selectionSet.add(id);
    } else {
      selectionSet.delete(id);
    }
  });

  return selectionSet.size;
};

export const countSelected = (selectionSet) =>
  selectionSet && typeof selectionSet.size === "number" ? selectionSet.size : 0;
